import { supabase } from './supabase';
import html2pdf from 'html2pdf.js';

/**
 * Generate and upload an UNPAID invoice for a newly created booking
 */
export async function generateUnpaidInvoice(
  bookingId: string,
  bookingData: any,
  businessData: any,
  customerData: any
): Promise<string | null> {
  try {
    console.log('🔄 [UNPAID INVOICE DEBUG] Starting unpaid invoice generation...');
    console.log('🔄 [UNPAID INVOICE DEBUG] Booking ID:', bookingId);
    
    // Create a payment-like object for unpaid invoice
    const unpaidPaymentData = {
      id: `unpaid-${bookingId}`,
      amount: 0, // No payment made yet
      status: 'unpaid',
      method: null,
      reference: null,
      notes: null,
      created_at: bookingData?.created_at || new Date().toISOString()
    };
    
    // Step 1: Generate PDF
    console.log('🔄 [UNPAID INVOICE DEBUG] Step 1: Creating unpaid invoice PDF...');
    const pdfBlob = await generatePDF(unpaidPaymentData, bookingData, businessData, customerData);
    console.log('✅ [UNPAID INVOICE DEBUG] Step 1 complete: PDF generated, size:', pdfBlob.size, 'bytes');

    // Step 2: Generate filename
    const filename = `unpaid-invoice-${bookingId}-${Date.now()}.pdf`;
    console.log('✅ [UNPAID INVOICE DEBUG] Step 2: Generated filename:', filename);

    // Step 3: Upload to Supabase Storage with custom URL
    console.log('🔄 [UNPAID INVOICE DEBUG] Step 3: Uploading to Supabase Storage...');
    const customerId = customerData?.id || customerData?.customer_id || 'unknown';
    
    const publicUrl = await storePDFInStorage(pdfBlob, filename, bookingId, customerId);
    console.log('✅ [UNPAID INVOICE DEBUG] Step 3&4 complete: Custom URL generated:', publicUrl);

    // Step 5: Create a special "unpaid" payment record with the PDF URL
    console.log('🔄 [UNPAID INVOICE DEBUG] Step 5: Creating unpaid payment record...');
    if (!supabase) throw new Error('Supabase client not initialized');
    
    const { data: unpaidPayment, error: paymentError } = await supabase
      .from('payments')
      .insert({
        business_id: businessData?.id || bookingData?.business_id,
        booking_id: bookingId,
        amount: 0.01, // Use 1 cent as placeholder to avoid constraint violation
        method: 'other',
        status: 'pending',
        reference: 'unpaid-invoice',
        notes: 'Unpaid invoice - automatically generated (placeholder amount)',
        pdf_url: publicUrl
      })
      .select()
      .single();

    if (paymentError) {
      console.error('❌ [UNPAID INVOICE DEBUG] Payment record creation failed:', paymentError);
      throw paymentError;
    }

    console.log('✅ [UNPAID INVOICE DEBUG] Unpaid invoice generation completed successfully');
    console.log('✅ [UNPAID INVOICE DEBUG] Final PDF URL:', publicUrl);

    return publicUrl;

  } catch (error) {
    console.error('❌ [UNPAID INVOICE DEBUG] Unpaid invoice generation failed:', error);
    return null;
  }
}

/**
 * Generate and upload a PDF receipt for a payment
 */
export async function generateAndUploadPDF(
  paymentId: string,
  paymentData: any,
  bookingData: any,
  businessData: any,
  customerData: any
): Promise<string | null> {
  try {
    console.log('🔄 [PDF DEBUG] Starting PDF generation process...');
    console.log('🔄 [PDF DEBUG] Payment ID:', paymentId);
    
    // Step 1: Generate PDF
    console.log('🔄 [PDF DEBUG] Step 1: Creating PDF...');
    const pdfBlob = await generatePDF(paymentData, bookingData, businessData, customerData);
    console.log('✅ [PDF DEBUG] Step 1 complete: PDF generated, size:', pdfBlob.size, 'bytes');

    // Step 2: Generate filename
    const filename = `invoice-${paymentId}-${Date.now()}.pdf`;
    console.log('✅ [PDF DEBUG] Step 2: Generated filename:', filename);

    // Step 3: Upload to Supabase Storage with custom URL
    console.log('🔄 [PDF DEBUG] Step 3: Uploading to Supabase Storage...');
    const customerId = customerData?.id || customerData?.customer_id || 'unknown';
    
    const publicUrl = await storePDFInStorage(pdfBlob, filename, bookingData?.id || 'unknown', customerId);
    console.log('✅ [PDF DEBUG] Step 3&4 complete: Custom URL generated:', publicUrl);

    // Step 5: Update payment record with PDF URL
    console.log('🔄 [PDF DEBUG] Step 5: Updating payment record...');
    if (!supabase) throw new Error('Supabase client not initialized');
    
    const { error: updateError } = await supabase
      .from('payments')
      .update({ pdf_url: publicUrl })
      .eq('id', paymentId);

    if (updateError) {
      console.error('❌ [PDF DEBUG] Payment update failed:', updateError);
      throw updateError;
    }

    console.log('✅ [PDF DEBUG] PDF generation completed successfully');
    console.log('✅ [PDF DEBUG] Final PDF URL:', publicUrl);

    return publicUrl;

  } catch (error) {
    console.error('❌ [PDF DEBUG] PDF generation failed:', error);
    return null;
  }
}

/**
 * Generate PDF using jsPDF - COMPREHENSIVE INVOICE DESIGN
 */
async function generatePDF(
  paymentData: any,
  bookingData: any,
  businessData: any,
  customerData: any
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    try {
      console.log('🔄 [PDF DEBUG] Starting comprehensive PDF generation...');
      
      // Access jsPDF through html2pdf library
      const tempElement = document.createElement('div');
      tempElement.innerHTML = '<p>temp</p>';
      document.body.appendChild(tempElement);

      const pdfInstance = html2pdf().from(tempElement).set({});
      
      pdfInstance.toPdf().get('pdf').then((pdf: any) => {
        console.log('✅ [PDF DEBUG] Successfully accessed jsPDF!');
        
        // Clean up temp element
        document.body.removeChild(tempElement);
        
        // Clear the PDF and start fresh
        pdf.deletePage(1);
        pdf.addPage();
        
        console.log('🎨 [PDF] Creating comprehensive invoice with all sections');
        
        // Set up page dimensions and page break handling
        const pageWidth = pdf.internal.pageSize.getWidth();
        const maxPageHeight = pdf.internal.pageSize.getHeight();
        const leftMargin = 20; // Very close to the edge
        const bottomMargin = 50; // Space reserved for summary section
        
        // STEP 1: Add "INVOICE" text on top left (all the way at the top)
        pdf.setFontSize(28);
        pdf.setFont(undefined, 'bold');
        pdf.setTextColor(0, 0, 0);
        pdf.text('INVOICE', leftMargin, 25); // Very close to the top
        
        // STEP 2: Add invoice number beside the INVOICE title
        const invoiceNumber = `#${bookingData?.booking_number || '1278'}`;
        const invoiceWidth = pdf.getTextWidth('INVOICE');
        pdf.setFontSize(24); // Slightly smaller than INVOICE title
        pdf.setFont(undefined, 'normal'); // Not bold
        pdf.setTextColor(100, 100, 100); // Gray color
        pdf.text(invoiceNumber, leftMargin + invoiceWidth + 8, 25); // 8px space after INVOICE
        
        // Set up page dimensions
        const centerX = pageWidth / 2;
        
        // STEP 3: Add "From" and "To" sections side by side
        let yPosition = 38; // Very close to the invoice title
        let fromYPosition = yPosition; // Track From section position
        let toYPosition = yPosition; // Track To section position
        
        // From header (left side)
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.setTextColor(0, 0, 0);
        pdf.text('From', leftMargin, fromYPosition);
        
        // To header (right side)
        pdf.text('To', centerX + 20, toYPosition);
        
        fromYPosition += 12; // Minimal space after "From"
        toYPosition += 12; // Minimal space after "To"
        
        // Business information (left side) - minimal spacing between lines
        pdf.setFontSize(11);
        pdf.setFont(undefined, 'normal');
        pdf.setTextColor(100, 100, 100);
        
        console.log('🏢 [PDF] Business data:', {
          name: businessData?.name,
          address: businessData?.address,
          phone: businessData?.phone,
          email: businessData?.email
        });
        
        if (businessData?.name) {
          pdf.text(businessData.name, leftMargin, fromYPosition);
          fromYPosition += 4; // 4px spacing
        }
        
        if (businessData?.address) {
          pdf.text(businessData.address, leftMargin, fromYPosition);
          fromYPosition += 4; // 4px spacing
        }
        
        if (businessData?.phone) {
          pdf.text(businessData.phone, leftMargin, fromYPosition);
          fromYPosition += 4; // 4px spacing
        }
        
        if (businessData?.email) {
          pdf.text(businessData.email, leftMargin, fromYPosition);
          fromYPosition += 4; // 4px spacing
        }
        
        // Customer information (right side) - only name, email, phone if they exist
        console.log('🔍 [PDF] Customer data:', {
          full_name: customerData?.full_name,
          name: customerData?.name,
          first_name: customerData?.first_name,
          last_name: customerData?.last_name,
          customer_name: customerData?.customer_name
        });
        
        // Try multiple possible name fields
        const customerName = customerData?.full_name || 
                            customerData?.name || 
                            customerData?.customer_name ||
                            (customerData?.first_name && customerData?.last_name 
                              ? `${customerData.first_name} ${customerData.last_name}` 
                              : customerData?.first_name || customerData?.last_name);
        
        if (customerName) {
          console.log('✅ [PDF] Using customer name:', customerName);
          pdf.text(customerName, centerX + 20, toYPosition);
          toYPosition += 4; // 4px spacing
        }
        
        if (customerData?.email) {
          pdf.text(customerData.email, centerX + 20, toYPosition);
          toYPosition += 4; // 4px spacing
        }
        
        // Try different phone field names
        const customerPhone = customerData?.phone || customerData?.phone_number || customerData?.mobile;
        if (customerPhone) {
          pdf.text(customerPhone, centerX + 20, toYPosition);
          toYPosition += 4; // 4px spacing
        }
        
        // Set yPosition to the maximum of both sections for future elements
        yPosition = Math.max(fromYPosition, toYPosition);
        
        console.log('✅ [PDF] Added INVOICE title, invoice number, From and To sections');
        
        // STEP 4: Add Services Section
        yPosition += 20; // Space between From/To and Services
        
        // Services header
        pdf.setFontSize(14);
        pdf.setFont(undefined, 'bold');
        pdf.setTextColor(0, 0, 0);
        pdf.text('Services', leftMargin, yPosition);
        yPosition += 15;
        
        // Services table/list - use actual booking data
        console.log('📋 [PDF] Booking data for services:', bookingData);
        console.log('📋 [PDF] Booking services:', bookingData?.booking_services);
        console.log('📋 [PDF] Services array:', bookingData?.services);
        
        // Get services data - use actual booking data
        let servicesArray = [];
        
        // First try: Use booking_services relation (most comprehensive)
        if (bookingData?.booking_services && Array.isArray(bookingData.booking_services)) {
          console.log('✅ [PDF] Using booking_services data');
          servicesArray = bookingData.booking_services.map((bookingService: any) => ({
            name: bookingService.services?.name || bookingService.service_name || 'Service',
            description: bookingService.services?.description || bookingService.description || '',
            vehicle: bookingData.vehicle || bookingService.vehicle || '',
            price: bookingService.price || bookingService.amount || 0,
            quantity: bookingService.quantity || 1
          }));
        }
        // Second try: Use direct services array from booking
        else if (bookingData?.services && Array.isArray(bookingData.services)) {
          console.log('✅ [PDF] Using direct services data');
          servicesArray = bookingData.services.map((service: any) => ({
            name: service.name || service.service_name || 'Service',
            description: service.description || '',
            vehicle: bookingData.vehicle || service.vehicle || '',
            price: service.price || service.amount || 0,
            quantity: service.quantity || 1
          }));
        }
        // Third try: Create single service from booking data
        else if (bookingData?.service_name || bookingData?.title) {
          console.log('✅ [PDF] Using single service from booking data');
          servicesArray = [{
            name: bookingData.service_name || bookingData.title,
            description: bookingData.service_description || bookingData.description || '',
            vehicle: bookingData.vehicle || '',
            price: bookingData.grand_total || bookingData.total_amount || 0,
            quantity: 1
          }];
        }
        
        // Final fallback
        if (servicesArray.length === 0) {
          console.log('⚠️ [PDF] No service data found, using fallback');
          servicesArray = [{
            name: 'Service',
            description: 'Professional service provided',
            vehicle: 'Vehicle information not available',
            price: bookingData?.grand_total || 0,
            quantity: 1
          }];
        }
        
        console.log('📋 [PDF] Final services array:', servicesArray);
        
        // Track page count for numbering
        let currentPage = 1;
        
        // Function to check if we need a new page
        const checkPageBreak = (additionalSpace: number = 0) => {
          if (yPosition + additionalSpace > maxPageHeight - bottomMargin) {
            console.log('📄 [PDF] Adding new page - content overflow detected');
            pdf.addPage();
            currentPage++;
            yPosition = 30; // Reset to top of new page with some margin
            return true;
          }
          return false;
        };
        
        // Draw services
        servicesArray.forEach((service: any, index: number) => {
          console.log(`📋 [PDF] Drawing service ${index + 1}:`, service);
          
          // Estimate space needed for this service (minimum 30px for service + description)
          let estimatedServiceHeight = 30;
          if (service.description) {
            const maxWidth = pageWidth - 80;
            const lines = pdf.splitTextToSize(service.description, maxWidth);
            estimatedServiceHeight += lines.length * 4 + 10; // Account for description lines
          }
          
          // Check if we need a new page before drawing this service
          checkPageBreak(estimatedServiceHeight);
          
          // Service name (bold, larger)
          pdf.setFontSize(12);
          pdf.setFont(undefined, 'bold');
          pdf.setTextColor(0, 0, 0);
          const serviceName = service.name || 'Service';
          pdf.text(serviceName, leftMargin, yPosition);
          
          // Service price (right aligned)
          const servicePrice = `$${(service.price || 0).toLocaleString()}`;
          const priceWidth = pdf.getTextWidth(servicePrice);
          pdf.text(servicePrice, pageWidth - 40 - priceWidth, yPosition);
          yPosition += 4; // 4px spacing
          
          // Vehicle information (if available)
          if (service.vehicle) {
            checkPageBreak(8); // Check before adding vehicle info
            pdf.setFontSize(9);
            pdf.setFont(undefined, 'normal');
            pdf.setTextColor(100, 100, 100);
            pdf.text(`Vehicle: ${service.vehicle}`, leftMargin, yPosition);
            yPosition += 4; // 4px spacing
          }
          
          // Service description (rich text handling)
          if (service.description) {
            pdf.setFontSize(8); // Smaller font size for description
            pdf.setFont(undefined, 'normal');
            pdf.setTextColor(80, 80, 80);
            
            // Strip HTML tags for basic rich text handling
            let description = service.description;
            if (typeof description === 'string') {
              // Remove HTML tags but keep the text content
              description = description.replace(/<[^>]*>/g, '');
              // Decode HTML entities
              description = description.replace(/&amp;/g, '&')
                                    .replace(/&lt;/g, '<')
                                    .replace(/&gt;/g, '>')
                                    .replace(/&quot;/g, '"')
                                    .replace(/&#39;/g, "'")
                                    .replace(/&nbsp;/g, ' ');
            }
            
            // Handle long descriptions with text wrapping
            const maxWidth = pageWidth - 80; // Leave margins
            const lines = pdf.splitTextToSize(description, maxWidth);
            
            lines.forEach((line: string) => {
              checkPageBreak(8); // Check before adding each description line
              pdf.text(line, leftMargin, yPosition);
              yPosition += 4; // 4px spacing for description lines
            });
          }
          
          yPosition += 4; // 4px spacing after each service
        });
        
        console.log('✅ [PDF] Added Services section');
        
        // STEP 5: Add Summary Section
        yPosition += 20; // Space between Services and Summary
        
        // Check if we have enough space for the Summary section (approximately 120px needed)
        const summarySpaceNeeded = 120;
        if (checkPageBreak(summarySpaceNeeded)) {
          console.log('📄 [PDF] Summary section moved to new page');
        }
        
        // Summary header
        pdf.setFontSize(14);
        pdf.setFont(undefined, 'bold');
        pdf.setTextColor(0, 0, 0);
        pdf.text('Summary', leftMargin, yPosition);
        yPosition += 15;
        
        // Check if this is an unpaid invoice
        const isUnpaidInvoice = paymentData?.status === 'unpaid' || paymentData?.amount === 0;
        
        // Calculate financial details
        console.log('💰 [PDF] Payment data:', paymentData);
        console.log('💰 [PDF] Booking financial data:', {
          grand_total: bookingData?.grand_total,
          total_amount: bookingData?.total_amount,
          subtotal: bookingData?.subtotal,
          tax_amount: bookingData?.tax_amount,
          tax_rate: bookingData?.tax_rate,
          tax_type: bookingData?.tax_type,
          discount_amount: bookingData?.discount_amount,
          discount_percentage: bookingData?.discount_percentage
        });
        
        const grandTotal = bookingData?.grand_total || bookingData?.total_amount || 0;
        const taxAmount = bookingData?.tax_amount || 0;
        const taxRate = bookingData?.tax_rate || 0;
        const taxType = bookingData?.tax_type || '';
        const discountAmount = bookingData?.discount_amount || 0;
        const discountPercentage = bookingData?.discount_percentage || 0;
        const paymentApplied = isUnpaidInvoice ? 0 : (paymentData?.amount || 0); // No payment for unpaid invoice
        
        console.log('💰 [PDF] Summary calculation:', {
          grandTotal,
          paymentApplied,
          currentPaymentOnly: paymentData?.currentPaymentAmount,
          balanceDue: Math.max(0, grandTotal - paymentApplied),
          isPaidInFull: (grandTotal - paymentApplied) <= 0,
          isUnpaidInvoice
        });
        
        // Calculate subtotal (before tax and discount)
        // If we have explicit subtotal, use it, otherwise calculate from grand total
        let subtotal = bookingData?.subtotal || 0;
        if (subtotal === 0) {
          // Calculate: grand total - tax + discount = subtotal before tax
          subtotal = grandTotal - taxAmount + discountAmount;
        }
        
        // Set up right-aligned column for amounts
        const rightColumn = pageWidth - 40;
        const leftColumn = pageWidth - 120;
        
        // Summary line styling
        pdf.setFontSize(11);
        pdf.setFont(undefined, 'normal');
        pdf.setTextColor(60, 60, 60);
        
        // Subtotal (before tax and discount)
        checkPageBreak(8); // Check before each summary line
        pdf.text('Subtotal:', leftColumn, yPosition);
        pdf.text(`$${subtotal.toLocaleString()}`, rightColumn - pdf.getTextWidth(`$${subtotal.toLocaleString()}`), yPosition);
        yPosition += 4;
        
        // Discount (only show if there's a discount)
        if (discountAmount > 0 || discountPercentage > 0) {
          checkPageBreak(8);
          let discountText = 'Discount:';
          let discountValue = '';
          
          if (discountPercentage > 0) {
            discountText = `Discount (${discountPercentage}%):`;
            discountValue = `-$${discountAmount.toLocaleString()}`;
          } else {
            discountValue = `-$${discountAmount.toLocaleString()}`;
          }
          
          pdf.setTextColor(0, 150, 0); // Green for discount
          pdf.text(discountText, leftColumn, yPosition);
          pdf.text(discountValue, rightColumn - pdf.getTextWidth(discountValue), yPosition);
          pdf.setTextColor(60, 60, 60); // Reset to gray
          yPosition += 4;
        }
        
        // Tax (applied after discount)
        if (taxAmount > 0) {
          checkPageBreak(8);
          // Build tax label with type and percentage
          let taxLabel = 'Tax:';
          if (taxType && taxRate > 0) {
            taxLabel = `${taxType} (${taxRate}%):`;
          } else if (taxType) {
            taxLabel = `${taxType}:`;
          } else if (taxRate > 0) {
            taxLabel = `Tax (${taxRate}%):`;
          }
          
          pdf.text(taxLabel, leftColumn, yPosition);
          pdf.text(`$${taxAmount.toLocaleString()}`, rightColumn - pdf.getTextWidth(`$${taxAmount.toLocaleString()}`), yPosition);
          yPosition += 4;
        }
        
        // Add line separator before total
        checkPageBreak(20);
        yPosition += 2;
        pdf.setDrawColor(200, 200, 200);
        pdf.line(leftColumn, yPosition, rightColumn, yPosition);
        yPosition += 6;
        
        // Grand Total (bold and larger)
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.setTextColor(0, 0, 0);
        pdf.text('Total:', leftColumn, yPosition);
        const totalText = `$${grandTotal.toLocaleString()}`;
        pdf.text(totalText, rightColumn - pdf.getTextWidth(totalText), yPosition);
        yPosition += 8;
        
        // Payment Applied (if there's a payment)
        if (paymentApplied > 0) {
          checkPageBreak(8);
          pdf.setFontSize(11);
          pdf.setFont(undefined, 'normal');
          pdf.setTextColor(0, 100, 200); // Blue for payment
          pdf.text('Payment Applied:', leftColumn, yPosition);
          const paymentText = `-$${paymentApplied.toLocaleString()}`;
          pdf.text(paymentText, rightColumn - pdf.getTextWidth(paymentText), yPosition);
          yPosition += 4;
        }
        
        // Balance Due (if any)
        const balanceDue = Math.max(0, grandTotal - paymentApplied);
        if (balanceDue > 0) {
          checkPageBreak(15);
          yPosition += 2;
          pdf.setDrawColor(200, 200, 200);
          pdf.line(leftColumn, yPosition, rightColumn, yPosition);
          yPosition += 6;
          
          pdf.setFontSize(12);
          pdf.setFont(undefined, 'bold');
          pdf.setTextColor(200, 0, 0); // Red for balance due
          pdf.text('Balance Due:', leftColumn, yPosition);
          const balanceText = `$${balanceDue.toLocaleString()}`;
          pdf.text(balanceText, rightColumn - pdf.getTextWidth(balanceText), yPosition);
        } else if (paymentApplied > 0) {
          // Paid in full
          checkPageBreak(15);
          yPosition += 2;
          pdf.setDrawColor(200, 200, 200);
          pdf.line(leftColumn, yPosition, rightColumn, yPosition);
          yPosition += 6;
          
          pdf.setFontSize(12);
          pdf.setFont(undefined, 'bold');
          pdf.setTextColor(0, 150, 0); // Green for paid
          pdf.text('PAID IN FULL', leftColumn, yPosition);
        }
        
        console.log('✅ [PDF] Added Summary section');
        
        // STEP 6: Add date stamp 
        yPosition += 30; // Add some space after summary
        const bottomRightX = pageWidth - 20; // 20px from right edge
        
        // Format date based on invoice type
        let dateText = '';
        if (isUnpaidInvoice) {
          // For unpaid invoices, show invoice generation date
          const invoiceDate = new Date(bookingData?.created_at || Date.now());
          const formattedDate = invoiceDate.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          });
          dateText = `Invoice generated ${formattedDate}`;
        } else {
          // For paid invoices, show payment date
          const paymentDate = new Date(paymentData?.created_at || Date.now());
          const formattedDate = paymentDate.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          });
          dateText = `Paid on ${formattedDate}`;
        }
        
        // Add date stamp
        pdf.setFontSize(9);
        pdf.setFont(undefined, 'normal');
        pdf.setTextColor(120, 120, 120); // Light gray
        const dateWidth = pdf.getTextWidth(dateText);
        pdf.text(dateText, bottomRightX - dateWidth, yPosition);
        
        console.log('✅ [PDF] Added date stamp');
        
        // STEP 7: Add page numbers to all pages
        const totalPages = pdf.internal.getNumberOfPages();
        console.log(`📄 [PDF] Adding page numbers: ${totalPages} total pages`);
        
        // Go through each page and add page numbers
        for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
          pdf.setPage(pageNum);
          
          // Add page number at bottom center
          const pageText = `Page ${pageNum}/${totalPages}`;
          pdf.setFontSize(9);
          pdf.setFont(undefined, 'normal');
          pdf.setTextColor(120, 120, 120); // Light gray
          
          const pageTextWidth = pdf.getTextWidth(pageText);
          const centerX = pageWidth / 2;
          const bottomY = maxPageHeight - 10; // 10px from bottom
          
          pdf.text(pageText, centerX - (pageTextWidth / 2), bottomY);
        }
        
        console.log('✅ [PDF] Added page numbers to all pages');
        console.log('🎨 [PDF DEBUG] Comprehensive invoice complete with all sections');
        
        // Convert to blob
        const pdfBlob = pdf.output('blob');
        resolve(pdfBlob);
        
      }).catch((error: any) => {
        console.error('❌ [PDF DEBUG] Failed to access jsPDF:', error);
        
        // Clean up temp element if it still exists
        if (document.body.contains(tempElement)) {
          document.body.removeChild(tempElement);
        }
        
        reject(error);
      });

    } catch (setupError) {
      console.error('💥 [PDF DEBUG] PDF setup failed:', setupError);
      reject(setupError);
    }
  });
}

/**
 * Format currency value
 */
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
}

// Store PDF in Supabase Storage and return public URL
async function storePDFInStorage(
  pdfBlob: Blob, 
  filename: string,
  bookingId: string,
  customerId: string
): Promise<string> {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }

  console.log('📂 [PDF] Storing PDF in storage:', filename);

  // Upload to Supabase Storage
  const { data, error } = await supabase.storage
    .from('receipts')
    .upload(filename, pdfBlob, {
      contentType: 'application/pdf',
      upsert: true
    });

  if (error) {
    console.error('❌ [PDF] Failed to upload PDF:', error);
    throw new Error(`Failed to upload PDF: ${error.message}`);
  }

  console.log('✅ [PDF] PDF uploaded successfully');

  // Get public URL and replace with custom domain
  const { data: urlData } = supabase.storage
    .from('receipts')
    .getPublicUrl(filename);

  // Replace the default Supabase URL with custom domain
  const publicUrl = urlData.publicUrl.replace(
    'https://idvgoctfaarxiryxprpy.supabase.co',
    'https://api.gitbooked.com'
  );
  
  console.log('✅ [PDF] Generated custom domain URL:', publicUrl);
  
  return publicUrl;
}



